import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { ErrorState } from "../components/States";
import { FullPageSpinner } from "../components/States";
import { useMe } from "../features/auth/hooks/useAuth";
import { useWorkspaces } from "../features/workspaces/hooks/useWorkspaces";
import { safeRedirectPath } from "../lib/redirect";
import { useUiStore } from "../stores/ui.store";

export function RequireAuth() {
  const me = useMe();
  const location = useLocation();

  if (me.isPending) return <FullPageSpinner />;
  // A failed background refresh must not sign the user out of the UI: only block when there is no
  // session to show. Transient failures were already retried with backoff (see `useMe`).
  if (me.isError && me.data === undefined) {
    return me.isFetching ? (
      <FullPageSpinner />
    ) : (
      <ErrorState error={me.error} onRetry={() => void me.refetch()} className="h-full" />
    );
  }
  if (!me.data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

export function GuestOnly() {
  const me = useMe();
  const [params] = useSearchParams();

  if (me.isPending) return <FullPageSpinner />;
  if (me.data) return <Navigate to={safeRedirectPath(params.get("next"))} replace />;
  return <Outlet />;
}

/**
 * `/`: signed-in people go to their workspace (see `HomeRedirect`); everyone else sees the
 * landing page. A failed session check also shows the landing page, whose links lead to sign-in.
 */
export function LandingGate() {
  const me = useMe();

  if (me.isPending) return <FullPageSpinner />;
  if (me.data) return <HomeRedirect />;
  return <Outlet />;
}

/** `/` → the most recently used workspace, the first workspace, or onboarding. */
export function HomeRedirect() {
  const workspaces = useWorkspaces();
  const lastSlug = useUiStore((state) => state.lastWorkspaceSlug);

  if (workspaces.isPending) return <FullPageSpinner />;
  if (workspaces.isError) {
    return (
      <ErrorState
        error={workspaces.error}
        onRetry={() => void workspaces.refetch()}
        className="h-full"
      />
    );
  }
  const target = workspaces.data.find((w) => w.slug === lastSlug) ?? workspaces.data[0];
  return <Navigate to={target ? `/w/${target.slug}` : "/onboarding"} replace />;
}
